/**
 * OutlinerCore.ts - OutlinerCore.js
 * 
 * CHANGE HISTORY:
 * - 251107_2135 [current] danielbeer: feat: Add OpCentral orchestrator integration (Actr, Aggregatr)
 * - 251021_0517 [8786ca9] danielbeer: feat: apply authr pattern to all 43 remaining packages
 * - 251021_0447 [ce7b5e6] danielbeer: feat: automate TypeScript and Actr integration for all packages
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
import { createOutlinerConfig, validateConfig, DISPLAY_MODES, SELECTION_MODES } from './OutlinerConfig';

export class OutlinerCore {
  config: Record<string, unknown>;
  actr: any;
  aggregatr: any;
  data: unknown;
  filteredData: any;
  sortedData: any;
  visibleNodes: any;
  selectedIds: any;
  lastSelectedId: any;
  anchorId: any;
  editingId: any;
  editingField: any;
  originalValue: any;
  sortColumns: any;
  filters: any;
  quickFilterText: any;
  expandedIds: any;
  columnWidths: any;
  columnOrder: any;
  subscribers: any;

  constructor(config = {}) {
    // Merge with defaults and validate
    this.config = createOutlinerConfig(config);
    const validation = validateConfig(this.config);
    
    if (!validation.valid) {
      console.error('Outliner configuration errors:', validation.errors);
      throw new Error('Invalid Outliner configuration');
    }
    
    // Store orchestrator references
    this.actr = this.config.actr;
    this.aggregatr = this.config.aggregatr;
    
    // Internal state
    this.data = [];
    this.filteredData = [];
    this.sortedData = [];
    this.visibleNodes = [];
    
    // Selection state
    this.selectedIds = new Set();
    this.lastSelectedId = null;
    this.anchorId = null; // For range selection
    
    // Edit state
    this.editingId = null;
    this.editingField = null;
    this.originalValue = null;
    
    // Sort state
    this.sortColumns = []; // [{column, direction}]
    
    // Filter state
    this.filters = {};
    this.quickFilterText = '';
    
    // Expansion state (if not stored in data)
    this.expandedIds = new Set();
    
    // Column state
    this.columnWidths = {};
    this.columnOrder = [];
    
    // Subscribers for state changes
    this.subscribers = [];
    
    // Initialize
    this.setData(this.config.data || []);
    this.initializeColumns();
  }
  
  /**
   * Initialize column configuration
   */
  initializeColumns(): any {
    this.config.columns.forEach(col => {
      this.columnWidths[col.id] = col.width;
    });
    
    this.columnOrder = this.config.columnOrder.length > 0
      ? this.config.columnOrder
      : this.config.columns.map(col => col.id);
  }
  
  /**
   * Set data and rebuild tree
   */
  async setData(data): Promise<any> {
    this.data = data;
    this.applyFiltersAndSort();
    this.updateVisibleNodes();
    this.notify();
    
    // Persist to Aggregatr if available
    if (this.aggregatr && this.config.workspaceId && this.config.datasetId) {
      try {
        await this.aggregatr.saveDataset(
          this.config.workspaceId,
          this.config.datasetId,
          data
        );
      } catch (error) {
        console.error('Aggregatr save failed:', error);
      }
    }
  }
  
  /**
   * Load data from Aggregatr
   */
  async loadData(): Promise<any> {
    if (this.aggregatr && this.config.workspaceId && this.config.datasetId) {
      try {
        const data = await this.aggregatr.loadDataset(
          this.config.workspaceId,
          this.config.datasetId
        );
        if (data) {
          await this.setData(data);
          return data;
        }
      } catch (error) {
        console.error('Aggregatr load failed:', error);
      }
    }
    return null;
  }
  
  /**
   * Get data
   */
  getData(): string {
    return this.data;
  }
  
  /**
   * Get item by ID
   */
  getItem(id): string {
    return this.findItemRecursive(this.data, id);
  }
  
  /**
   * Find item recursively
   */
  findItemRecursive(items, id): any {
    for (const item of items) {
      if (item[this.config.idField] === id) {
        return item;
      }
      
      const children = item[this.config.childrenField];
      if (children && children.length > 0) {
        const found = this.findItemRecursive(children, id);
        if (found) return found;
      }
    }
    return null;
  }
  
  /**
   * Get children of an item
   */
  getChildren(id): string {
    if (!id) {
      return this.data;
    }
    
    const item = this.getItem(id);
    return item ? (item[this.config.childrenField] || []) : [];
  }
  
  /**
   * Check if item is expanded
   */
  isExpanded(id): boolean {
    const item = this.getItem(id);
    if (!item) return false;
    
    // Check data field first
    if (this.config.expandedField && item[this.config.expandedField] !== undefined) {
      return item[this.config.expandedField];
    }
    
    // Fall back to internal state
    return this.expandedIds.has(id);
  }
  
  /**
   * Toggle expansion
   */
  toggleExpanded(id): any {
    const item = this.getItem(id);
    if (!item) return;
    
    const isCurrentlyExpanded = this.isExpanded(id);
    
    // Update data field if configured
    if (this.config.expandedField) {
      item[this.config.expandedField] = !isCurrentlyExpanded;
    }
    
    // Update internal state
    if (isCurrentlyExpanded) {
      this.expandedIds.delete(id);
    } else {
      this.expandedIds.add(id);
    }
    
    this.updateVisibleNodes();
    this.notify();
    
    // Call callback
    if (isCurrentlyExpanded && this.config.onCollapse) {
      this.config.onCollapse(id, item);
    } else if (!isCurrentlyExpanded && this.config.onExpand) {
      this.config.onExpand(id, item);
    }
  }
  
  /**
   * Get all visible nodes (respecting expansion state)
   */
  getAllVisibleNodes(): string {
    return this.visibleNodes;
  }
  
  /**
   * Update visible nodes based on expansion state
   */
  updateVisibleNodes(): any {
    this.visibleNodes = [];
    this.collectVisibleNodes(this.sortedData, 0);
  }
  
  /**
   * Recursively collect visible nodes
   */
  collectVisibleNodes(items, level): any {
    items.forEach(item => {
      const id = item[this.config.idField];
      
      // Add this item
      this.visibleNodes.push({
        ...item,
        _level: level,
        _hasChildren: this.hasChildren(item),
        _isExpanded: this.isExpanded(id)
      });
      
      // Add children if expanded
      if (this.isExpanded(id)) {
        const children = item[this.config.childrenField];
        if (children && children.length > 0) {
          this.collectVisibleNodes(children, level + 1);
        }
      }
    });
  }
  
  /**
   * Check if item has children
   */
  hasChildren(item): boolean {
    if (this.config.hasChildrenField && item[this.config.hasChildrenField] !== undefined) {
      return item[this.config.hasChildrenField];
    }
    
    const children = item[this.config.childrenField];
    return children && children.length > 0;
  }
  
  /**
   * Selection methods
   */
  select(id, options = {}): any {
    const { multi = false, range = false, toggle = false } = options;
    
    if (this.config.selectionMode === SELECTION_MODES.NONE) {
      return;
    }
    
    if (this.config.selectionMode === SELECTION_MODES.SINGLE) {
      this.selectedIds.clear();
      this.selectedIds.add(id);
      this.lastSelectedId = id;
      this.anchorId = id;
    } else if (toggle) {
      if (this.selectedIds.has(id)) {
        this.selectedIds.delete(id);
      } else {
        this.selectedIds.add(id);
        this.lastSelectedId = id;
      }
    } else if (range && this.anchorId) {
      this.selectRange(this.anchorId, id);
    } else if (multi) {
      this.selectedIds.add(id);
      this.lastSelectedId = id;
      this.anchorId = id;
    } else {
      this.selectedIds.clear();
      this.selectedIds.add(id);
      this.lastSelectedId = id;
      this.anchorId = id;
    }
    
    this.notify();
    
    if (this.config.onSelect) {
      this.config.onSelect(Array.from(this.selectedIds), id);
    }
  }
  
  /**
   * Select range between two IDs
   */
  selectRange(fromId, toId): any {
    const fromIndex = this.visibleNodes.findIndex(n => n[this.config.idField] === fromId);
    const toIndex = this.visibleNodes.findIndex(n => n[this.config.idField] === toId);
    
    if (fromIndex === -1 || toIndex === -1) return;
    
    const start = Math.min(fromIndex, toIndex);
    const end = Math.max(fromIndex, toIndex);
    
    for (let i = start; i <= end; i++) {
      this.selectedIds.add(this.visibleNodes[i][this.config.idField]);
    }
    
    this.lastSelectedId = toId;
  }
  
  /**
   * Clear selection
   */
  clearSelection(): any {
    this.selectedIds.clear();
    this.lastSelectedId = null;
    this.anchorId = null;
    this.notify();
  }
  
  /**
   * Get selection
   */
  getSelection(): string {
    return {
      ids: Array.from(this.selectedIds),
      lastId: this.lastSelectedId,
      count: this.selectedIds.size
    };
  }
  
  /**
   * Sorting methods
   */
  sort(columnId, direction = 'asc', append = false): any {
    if (!append) {
      this.sortColumns = [];
    }
    
    // Remove existing sort for this column
    this.sortColumns = this.sortColumns.filter(s => s.column !== columnId);
    
    // Add new sort
    this.sortColumns.push({ column: columnId, direction });
    
    this.applyFiltersAndSort();
    this.updateVisibleNodes();
    this.notify();
    
    if (this.config.onSort) {
      this.config.onSort(this.sortColumns);
    }
  }
  
  /**
   * Apply filters and sorting
   */
  applyFiltersAndSort(): any {
    // Start with all data
    let result = [...this.data];
    
    // Apply filters
    result = this.applyFilters(result);
    
    // Apply sorting
    result = this.applySorting(result);
    
    this.filteredData = result;
    this.sortedData = result;
  }
  
  /**
   * Apply filters recursively
   */
  applyFilters(items): any {
    // TODO: Implement filtering logic
    return items;
  }
  
  /**
   * Apply sorting recursively
   */
  applySorting(items): any {
    if (this.sortColumns.length === 0) {
      return items;
    }
    
    const sorted = [...items].sort((a, b) => {
      for (const { column, direction } of this.sortColumns) {
        const col = this.config.columns.find(c => c.id === column);
        if (!col) continue;
        
        const aVal = this.getFieldValue(a, col.field);
        const bVal = this.getFieldValue(b, col.field);
        
        let comparison = 0;
        if (aVal < bVal) comparison = -1;
        if (aVal > bVal) comparison = 1;
        
        if (comparison !== 0) {
          return direction === 'asc' ? comparison : -comparison;
        }
      }
      return 0;
    });
    
    // Recursively sort children
    sorted.forEach(item => {
      const children = item[this.config.childrenField];
      if (children && children.length > 0) {
        item[this.config.childrenField] = this.applySorting(children);
      }
    });
    
    return sorted;
  }
  
  /**
   * Get field value from item
   */
  getFieldValue(item, field): string {
    if (typeof field === 'function') {
      return field(item);
    }
    return item[field];
  }
  
  /**
   * Navigation methods
   */
  navigateUp(): any {
    if (!this.lastSelectedId) return null;
    
    const currentIndex = this.visibleNodes.findIndex(n => n[this.config.idField] === this.lastSelectedId);
    if (currentIndex > 0) {
      const newId = this.visibleNodes[currentIndex - 1][this.config.idField];
      this.select(newId);
      return newId;
    }
    return null;
  }
  
  navigateDown(): any {
    if (!this.lastSelectedId) {
      // Select first
      if (this.visibleNodes.length > 0) {
        const newId = this.visibleNodes[0][this.config.idField];
        this.select(newId);
        return newId;
      }
      return null;
    }
    
    const currentIndex = this.visibleNodes.findIndex(n => n[this.config.idField] === this.lastSelectedId);
    if (currentIndex < this.visibleNodes.length - 1) {
      const newId = this.visibleNodes[currentIndex + 1][this.config.idField];
      this.select(newId);
      return newId;
    }
    return null;
  }
  
  /**
   * Subscribe to state changes
   */
  subscribe(callback): any {
    this.subscribers.push(callback);
    
    // Return unsubscribe function
    return () => {
      this.subscribers = this.subscribers.filter(cb => cb !== callback);
    };
  }
  
  /**
   * Notify subscribers of state change
   */
  notify(): any {
    const state = this.getState();
    this.subscribers.forEach(callback => callback(state));
  }
  
  /**
   * Tree operations
   */
  async addChild(parentId, data = {}): Promise<any> {
    // Route through Actr if available
    if (this.actr) {
      try {
        const result = await this.actr.execute('outliner:add-child', {
          parentId,
          data,
          workspaceId: this.config.workspaceId,
          datasetId: this.config.datasetId
        });
        this.updateVisibleNodes();
        this.notify();
        return result.id;
      } catch (error) {
        console.error('Actr add-child failed:', error);
        // Fall through to callback or internal
      }
    }
    
    // If callback provided, use it (for external data management)
    if (this.config.onAddChild) {
      const newId = this.config.onAddChild(parentId, data);
      this.updateVisibleNodes();
      this.notify();
      return newId;
    }
    
    // Otherwise manage internally
    const newItem = {
      [this.config.idField]: crypto.randomUUID(),
      [this.config.hierarchyField]: parentId,
      ...data
    };
    
    // Add to parent's children
    if (parentId) {
      const parent = this.getItem(parentId);
      if (parent) {
        if (!parent[this.config.childrenField]) {
          parent[this.config.childrenField] = [];
        }
        parent[this.config.childrenField].push(newItem);
      }
    } else {
      // Add to root
      this.data.push(newItem);
    }
    
    this.updateVisibleNodes();
    this.notify();
    
    return newItem[this.config.idField];
  }
  
  async addSibling(nodeId, data = {}, position = 'after'): Promise<any> {
    // Route through Actr if available
    if (this.actr) {
      try {
        const result = await this.actr.execute('outliner:add-sibling', {
          nodeId,
          data,
          position,
          workspaceId: this.config.workspaceId,
          datasetId: this.config.datasetId
        });
        this.updateVisibleNodes();
        this.notify();
        return result.id;
      } catch (error) {
        console.error('Actr add-sibling failed:', error);
        // Fall through to callback or internal
      }
    }
    
    // If callback provided, use it (for external data management)
    if (this.config.onAddSibling) {
      const newId = this.config.onAddSibling(nodeId, data);
      this.updateVisibleNodes();
      this.notify();
      return newId;
    }
    
    const node = this.getItem(nodeId);
    if (!node) return null;
    
    const parentId = node[this.config.hierarchyField];
    const newItem = {
      [this.config.idField]: crypto.randomUUID(),
      [this.config.hierarchyField]: parentId,
      ...data
    };
    
    // Get siblings
    const siblings = parentId ? this.getChildren(parentId) : this.data;
    const currentIndex = siblings.findIndex(s => s[this.config.idField] === nodeId);
    
    if (currentIndex !== -1) {
      const insertIndex = position === 'before' ? currentIndex : currentIndex + 1;
      siblings.splice(insertIndex, 0, newItem);
    } else {
      siblings.push(newItem);
    }
    
    this.updateVisibleNodes();
    this.notify();
    
    return newItem[this.config.idField];
  }
  
  async deleteNode(nodeId): Promise<any> {
    // Route through Actr if available
    if (this.actr) {
      try {
        await this.actr.execute('outliner:delete-node', {
          nodeId,
          workspaceId: this.config.workspaceId,
          datasetId: this.config.datasetId
        });
        this.updateVisibleNodes();
        this.notify();
        return true;
      } catch (error) {
        console.error('Actr delete-node failed:', error);
        // Fall through to callback or internal
      }
    }
    
    // If callback provided, use it (for external data management)
    if (this.config.onDelete) {
      this.config.onDelete(nodeId);
      this.updateVisibleNodes();
      this.notify();
      return true;
    }
    
    const node = this.getItem(nodeId);
    if (!node) return false;
    
    const parentId = node[this.config.hierarchyField];
    const siblings = parentId ? this.getChildren(parentId) : this.data;
    const index = siblings.findIndex(s => s[this.config.idField] === nodeId);
    
    if (index !== -1) {
      siblings.splice(index, 1);
      this.updateVisibleNodes();
      this.notify();
      return true;
    }
    
    return false;
  }
  
  async moveNode(nodeId, toParentId, toIndex): Promise<any> {
    // Route through Actr if available
    if (this.actr) {
      try {
        await this.actr.execute('outliner:move-node', {
          nodeId,
          toParentId,
          toIndex,
          workspaceId: this.config.workspaceId,
          datasetId: this.config.datasetId
        });
        this.updateVisibleNodes();
        this.notify();
        return true;
      } catch (error) {
        console.error('Actr move-node failed:', error);
        // Fall through to callback or internal
      }
    }
    
    // If callback provided, use it (for external data management)
    if (this.config.onMove) {
      this.config.onMove(nodeId, toParentId, toIndex);
      this.updateVisibleNodes();
      this.notify();
      return true;
    }
    
    const node = this.getItem(nodeId);
    if (!node) return false;
    
    const fromParentId = node[this.config.hierarchyField];
    
    // Remove from old parent
    const fromSiblings = fromParentId ? this.getChildren(fromParentId) : this.data;
    const fromIndex = fromSiblings.findIndex(s => s[this.config.idField] === nodeId);
    
    if (fromIndex !== -1) {
      fromSiblings.splice(fromIndex, 1);
    }
    
    // Update parent reference
    node[this.config.hierarchyField] = toParentId;
    
    // Add to new parent
    const toSiblings = toParentId ? this.getChildren(toParentId) : this.data;
    toSiblings.splice(toIndex, 0, node);
    
    this.updateVisibleNodes();
    this.notify();
    
    return true;
  }
  
  indent(nodeId): any {
    const node = this.getItem(nodeId);
    if (!node) return false;
    
    const parentId = node[this.config.hierarchyField];
    const siblings = parentId ? this.getChildren(parentId) : this.data;
    const currentIndex = siblings.findIndex(s => s[this.config.idField] === nodeId);
    
    if (currentIndex > 0) {
      const previousSibling = siblings[currentIndex - 1];
      return this.moveNode(nodeId, previousSibling[this.config.idField], 0);
    }
    
    return false;
  }
  
  outdent(nodeId): any {
    const node = this.getItem(nodeId);
    if (!node) return false;
    
    const parentId = node[this.config.hierarchyField];
    if (!parentId) return false; // Already at root
    
    const parent = this.getItem(parentId);
    if (!parent) return false;
    
    const grandparentId = parent[this.config.hierarchyField];
    const parentSiblings = grandparentId ? this.getChildren(grandparentId) : this.data;
    const parentIndex = parentSiblings.findIndex(s => s[this.config.idField] === parentId);
    
    return this.moveNode(nodeId, grandparentId, parentIndex + 1);
  }

  /**
   * Navigate to parent
   */
  navigateToParent(nodeId): any {
    const node = this.getItem(nodeId);
    if (!node) return null;
    
    const parentId = node[this.config.hierarchyField];
    if (parentId) {
      this.select(parentId);
      return parentId;
    }
    return null;
  }
  
  /**
   * Navigate to first child
   */
  navigateToFirstChild(nodeId): any {
    const children = this.getChildren(nodeId);
    if (children.length > 0 && this.isExpanded(nodeId)) {
      this.select(children[0][this.config.idField]);
      return children[0][this.config.idField];
    }
    return null;
  }

  /**
   * Get current state
   */
  getState(): string {
    return {
      data: this.data,
      visibleNodes: this.visibleNodes,
      selection: this.getSelection(),
      editing: {
        id: this.editingId,
        field: this.editingField
      },
      sort: this.sortColumns,
      filters: this.filters,
      columns: {
        widths: this.columnWidths,
        order: this.columnOrder
      }
    };
  }
}

export default OutlinerCore;
